import { Button, Heading, Icon } from '@innovationnorway/cl-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import AddEntityModalButton from 'src/components/AddEntityModalButton/AddEntityModalButton';
import ContentPage from 'src/components/ContentPage/ContentPage';
import { LoadingPage } from 'src/components/LoadingPage/LoadingPage';
import { EventFormWrapper } from 'src/features/projects';
import useEvent from 'src/hooks/use-event';
import { formatDateToNOString } from 'src/utils/FormatValue';
import EventParticipantsTable from '../../components/EventParticipantsTable/EventParticipantsTable';
import EventSidebar from '../../components/EventSidebar/EventSidebar';
import css from './EventDetails.module.scss';
import NotFoundPage from 'src/components/NotFoundPage/NotFoundPage';
import useProjects from 'src/features/projects/hooks/use-projects';
type PageParams = {
  eventId: string;
};
const EventDetails: React.FC = () => {
  const { eventId = '' } = useParams<PageParams>();
  const { event, isLoading } = useEvent(eventId);
  const { project } = useProjects(event?.projectId);
  const navigate = useNavigate();
  const { t: tCommon } = useTranslation('common');
  const { t: tEvents } = useTranslation('events');
  if (isLoading) {
    return <LoadingPage />;
  }
  if (!event) {
    return <NotFoundPage />;
  }
  return (
    <ContentPage title={event.name} breadcrumbLeaf={event.name} width="wide">
      {project && (
        <Button
          type="button"
          variant="tertiary"
          onClick={() => navigate(`/projects/details/${project?.id}/events`)}
        >
          <Icon prefix="fasr" iconName="long-arrow-alt-left" size="s" className="margin-right--1" />
          {project?.name}
        </Button>
      )}
      <div className={css['layout']}>
        <div className={css['event-details']}>
          <div>
            <div className={css['event-details__info']}>
              <div className={css['event-details__info--dates']}>
                <span>
                  {tCommon('startDate')}:{' '}
                  {formatDateToNOString(
                    event?.startDate ? event.startDate : new Date(),
                    false,
                    '{date}.{month}.{year}',
                    true,
                  )}
                  {event?.endDate && (
                    <>
                      {' '}
                      | {tCommon('endDate')}:{' '}
                      {formatDateToNOString(event.endDate, false, '{date}.{month}.{year}', true)}
                    </>
                  )}
                </span>
                <span className={css['event-details__info--edit-button']}>
                  <AddEntityModalButton
                    buttonVariant="secondary"
                    buttonText={
                      <>
                        <Icon prefix="fasr" iconName="edit" size="xs" className={'margin-right--1'} />
                        {tEvents('editEvent')}
                      </>
                    }
                    modalTitle={tEvents('editEvent')}
                  >
                    <EventFormWrapper project={project} event={event} />
                  </AddEntityModalButton>
                </span>
              </div>
              <p className={css['event-details__info--text']}>
                <Icon
                  prefix="fasr"
                  iconName="map-marker-alt"
                  size="xs"
                  className={css['event-details__info--icon']}
                />
                {event?.location}
              </p>
              <p className={css['event-details__info--text']}>
                <Icon prefix="fasr" iconName="clock" size="xs" className={css['event-details__info--icon']} />
                {event?.duration} {tCommon('hours')}
              </p>
            </div>
          </div>
          <div className={css['event-details__description']}>
            <Heading level={2}>{tEvents('eventDescription')}</Heading>
            <p>{event?.description}</p>
          </div>
        </div>
        <aside className={css['event-details__sidebar']}>{event && <EventSidebar event={event} />}</aside>
        <div className={css['event-details__participants-table']}>
          <Heading level={2}>{tEvents('eventParticipants')}</Heading>
          <EventParticipantsTable eventId={event?.id ? event.id : ''} />
        </div>
      </div>
    </ContentPage>
  );
};
export default EventDetails;
